import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from "@angular/router";
import { Guid, IGroupDto, IUserDto } from "moo-shared";
import { EMPTY, map, Observable, of, switchMap, tap } from "rxjs";
import { AuthenticationApiService, IAuthApiGroupDto } from "../../core/authentication/authentication-api.service";
import { IIpOrganisationDto } from "../../core/authentication/ip-organisation.dto";
import { IpTokenService } from "../../core/authentication/iptoken.service";
import { ResourceApiService } from "../../core/authentication/resource-api.service";
import { routePaths } from "../../routes";
import { isDefined, isEmptyString } from "../../shared/helpers";
import { ButtonTile } from "../../shared/models/button-tile.model";
import { ITile } from "../../shared/models/tile.model";

@Injectable()
export class LoginAssistantService {
  private currentSelectedGroup: ITile | undefined = undefined;
  selectGroup(group: ITile) {
    this.currentSelectedGroup = group;
  }
  get selectedGroup(): ITile | undefined {
    return this.currentSelectedGroup;
  }
  clearGroup() {
    this.currentSelectedGroup = undefined;
  }

  private currentSelectedOrganisationKey: Guid | "" = "";
  setSelectedOrganisationKey(organisationKey: Guid): void {
    this.currentSelectedOrganisationKey = organisationKey;
  }
  clearOrganisation(): void {
    this.clearGroup();
    this.currentSelectedOrganisationKey = "";
  }

  get selectedOrganisationKey(): Guid | "" {
    return this.currentSelectedOrganisationKey;
  }

  constructor(
    private readonly resourceApiService: ResourceApiService,
    private readonly authApiService: AuthenticationApiService,
    private readonly ipTokenService: IpTokenService,
    private readonly router: Router,
  ) { }

  redirectToLastAssistantScreen() {
    const selectedGroup = this.selectedGroup;

    if (!this.hasLoginAssistantHistory()) {
      this.navigateToLogin();
      return;
    }

    if (selectedGroup && !this.isFunctionalTileKey(selectedGroup.key)) {
      this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.groups}/${selectedGroup.key}`]);
      return;
    }

    this.navigateToGroups();
  }

  hasLoginAssistantHistory(): boolean {
    const selectedGroup = this.selectedGroup;

    if (!selectedGroup) {
      return false;
    }

    if (!this.isFunctionalTileKey(selectedGroup.key)) {
      return true;
    }

    return !isEmptyString(this.selectedOrganisationKey);
  }

  navigateToLogin(): void {
    this.router.navigate(["/"]);
  }

  navigateToGroups(): void {
    this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.groups}`]);
  }

  navigateToOrganisationSelection(): void {
    this.router.navigate([`/${routePaths.loginAssistant}/${routePaths.organisations}`]);
  }

  getSelectedOrganisation(): Observable<IIpOrganisationDto> {
    return this.withOrganisationKey((organisationKey) =>
      this.ipTokenService.getAvailableOrganisations().pipe(
        map((organisations) => organisations.find((organisation) => organisation.Key === organisationKey)!)
      )
    );
  }

  getOrganizationGroups(): Observable<IGroupDto[]> {
    return this.withOrganisationKey((organisationKey) => this.resourceApiService.getOrganisationStudentsGroups(organisationKey));
  }

  getAuthApiOrganizationGroups(): Observable<IAuthApiGroupDto[]> {
    return this.withOrganisationKey((organisationKey) => this.authApiService.getGroups(organisationKey));
  }

  //can't use route injected in this service as it does not have accurate info(at needed moment) so will use route injected in  the resolver
  getGroupStudents(route: ActivatedRouteSnapshot): Observable<IUserDto[]> {
    const groupKey = route.paramMap.get("groupKey")!;
    return this.resourceApiService.getGroupStudents(groupKey);
  }

  canActivateLoginAssistant(currentUrl: string): Observable<boolean | UrlTree> {
    return this.ipTokenService.getIpToken().pipe(
      map((token) => {
        if (!isEmptyString(token)) {
          return this.isAssistantRootUrl(currentUrl) ? this.groupsUrlTree() : true;
        }

        if (this.ipTokenService.isOrganisationSelectionRequired()) {
          return this.isAssistantRootUrl(currentUrl) ? this.organisationsUrlTree() : true;
        }

        return this.loginUrlTree();
      })
    );
  }

  canActivateGroupList(): boolean | UrlTree {
    if (!isEmptyString(this.ipTokenService.currentToken)) {
      return true;
    }

    if (this.ipTokenService.isOrganisationSelectionRequired()) {
      return this.organisationsUrlTree();
    }

    return this.loginUrlTree();
  }

  canActivateOrganisationList(): Observable<boolean | UrlTree> {
    if (this.ipTokenService.hasMultipleOrganisations()) {
      return of(true);
    }

    if (!isEmptyString(this.ipTokenService.currentToken)) {
      return of(this.groupsUrlTree());
    }

    return this.ipTokenService.getAvailableOrganisations().pipe(
      map((organisations: IIpOrganisationDto[]) => organisations.length > 1 ? true : this.loginUrlTree())
    );
  }

  canActivateGroupDetails(): boolean | UrlTree {
    if (isDefined(this.selectedGroup)) {
      return true;
    }

    return this.groupsUrlTree();
  }

  private isAssistantRootUrl(currentUrl: string): boolean {
    const path = currentUrl.split("?")[0].split("#")[0];
    return path === `/${routePaths.loginAssistant}` || path === `/${routePaths.loginAssistant}/`;
  }

  private loginUrlTree(): UrlTree {
    return this.router.createUrlTree(["/"]);
  }

  private organisationsUrlTree(): UrlTree {
    return this.router.createUrlTree([`/${routePaths.loginAssistant}/${routePaths.organisations}`]);
  }

  private groupsUrlTree(): UrlTree {
    return this.router.createUrlTree([`/${routePaths.loginAssistant}/${routePaths.groups}`]);
  }

  private isFunctionalTileKey(key: string): boolean {
    return [ButtonTile.PersonnelTileKey, ButtonTile.QrTileKey, ButtonTile.BackTileKey].includes(key);
  }

  private withOrganisationKey<T>(getter: (organisationKey: Guid) => Observable<T>): Observable<T> {
    return this.resolveOrganisationKey().pipe(
      switchMap((organisationKey) => {
        if (isEmptyString(organisationKey)) {
          this.navigateToLogin();
          return EMPTY;
        }

        return getter(organisationKey);
      })
    );
  }

  private resolveOrganisationKey(): Observable<Guid | ""> {
    if (!isEmptyString(this.currentSelectedOrganisationKey)) {
      return of(this.currentSelectedOrganisationKey);
    }

    return this.ipTokenService.getAvailableOrganisations().pipe(
      map((organisations: IIpOrganisationDto[]) => organisations.length === 1 ? organisations[0].Key : ""),
      tap((organisationKey: Guid | "") => {
        this.currentSelectedOrganisationKey = organisationKey;
      })
    );
  }
}

export const canActivateLoginAssistant: CanActivateFn = (_, state) => {
  return inject(LoginAssistantService).canActivateLoginAssistant(state.url);
};

export const canActivateGroupDetails: CanActivateFn = () => {
  return inject(LoginAssistantService).canActivateGroupDetails();
};

export const canActivateGroupList: CanActivateFn = () => {
  return inject(LoginAssistantService).canActivateGroupList();
};

export const canActivateOrganisationList: CanActivateFn = () => {
  return inject(LoginAssistantService).canActivateOrganisationList();
};
